import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { fetchVideos, approveVideo, rejectVideo, unpublishVideo, type AdminVideo } from '../lib/videoService';
import { logger } from '../lib/logger';

type TabKey = 'pending_review' | 'approved' | 'rejected';

const tabLabels: { key: TabKey; label: string }[] = [
  { key: 'pending_review', label: 'En attente' },
  { key: 'approved', label: 'Approuvées' },
  { key: 'rejected', label: 'Rejetées' },
];

const VideoModeration: React.FC = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState<AdminVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('pending_review');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  /** Publication ouverte dans le lecteur plein écran (vidéo ou image). */
  const [playing, setPlaying] = useState<AdminVideo | null>(null);
  /** Échec de lecture (codec non supporté par le navigateur, ex. HEVC iPhone). */
  const [playError, setPlayError] = useState(false);

  useEffect(() => {
    fetchVideos().then(setVideos).catch((err) => logger.error('load_videos', 'VideoModeration', { error: String(err) })).finally(() => setLoading(false));
  }, []);

  const pendingCount = videos.filter((v) => v.status === 'pending_review').length;
  const filteredVideos = videos.filter((v) => v.status === activeTab);

  const handleApprove = async (id: string) => {
    if (!user) return;
    setActionLoading(id);
    try {
      await approveVideo(id, user.id);
      setVideos((prev) =>
        prev.map((v) => v.id === id ? { ...v, status: 'approved' as const, reviewedAt: new Date().toLocaleDateString('fr-FR') } : v)
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    if (!user || !rejectReason.trim()) return;
    setActionLoading(id);
    try {
      await rejectVideo(id, user.id, rejectReason);
      setVideos((prev) =>
        prev.map((v) => v.id === id ? { ...v, status: 'rejected' as const, rejectionReason: rejectReason, reviewedAt: new Date().toLocaleDateString('fr-FR') } : v)
      );
      setRejectingId(null);
      setRejectReason('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async (id: string) => {
    if (!user) return;
    setActionLoading(id);
    try {
      await unpublishVideo(id, user.id);
      setVideos((prev) =>
        prev.map((v) => v.id === id ? { ...v, status: 'pending_review' as const, rejectionReason: null } : v)
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDuration = (s: number | null) => {
    if (!s) return '—';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-xl border border-orange-200 px-5 py-3 flex items-center gap-3">
          <span className="text-2xl">🎬</span>
          <div>
            <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
            <p className="text-xs text-gray-500">en attente</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 px-5 py-3 flex items-center gap-3">
          <span className="text-2xl">✅</span>
          <div>
            <p className="text-2xl font-bold text-blue-600">{videos.filter(v => v.status === 'approved').length}</p>
            <p className="text-xs text-gray-500">approuvées</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-200 px-5 py-3 flex items-center gap-3">
          <span className="text-2xl">❌</span>
          <div>
            <p className="text-2xl font-bold text-red-600">{videos.filter(v => v.status === 'rejected').length}</p>
            <p className="text-xs text-gray-500">rejetées</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {tabLabels.map((tab) => {
            const count = videos.filter((v) => v.status === tab.key).length;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-[#00244F] bg-blue-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                  activeTab === tab.key ? 'bg-[#00244F] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {count}
                </span>
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00244F]"></span>
                )}
              </button>
            );
          })}
        </div>

        <div className="divide-y divide-gray-100">
          {filteredVideos.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-400">
              Aucune vidéo dans cette catégorie
            </div>
          ) : (
            filteredVideos.map((video) => (
              <div key={video.id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50/50 transition-colors">
                {/* Vignette cliquable → lecteur plein écran */}
                <button
                  onClick={() => { setPlayError(false); setPlaying(video); }}
                  className="w-24 h-16 rounded-lg flex items-center justify-center text-white text-2xl flex-shrink-0 relative overflow-hidden bg-gray-300 group cursor-pointer"
                  title="Visionner"
                >
                  {video.thumbnailUrl ? (
                    <img src={video.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="opacity-70">▶</span>
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-8 h-8 rounded-full bg-white/90 text-[#00244F] flex items-center justify-center text-sm">▶</span>
                  </span>
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1 rounded">
                    {video.mediaType === 'image' ? 'IMG' : formatDuration(video.durationSeconds)}
                  </span>
                </button>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{video.candidateName}</p>
                  <p className="text-xs text-gray-400 mt-1">Soumise le {video.submittedAt}</p>
                  {video.reviewedAt && (
                    <p className="text-xs text-gray-400">Revue le {video.reviewedAt}</p>
                  )}
                  {video.rejectionReason && (
                    <p className="text-xs text-red-500 mt-1">Motif: {video.rejectionReason}</p>
                  )}
                </div>

                {video.status === 'pending_review' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {rejectingId === video.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Motif du rejet..."
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                          autoFocus
                        />
                        <button
                          onClick={() => handleReject(video.id)}
                          disabled={!rejectReason.trim() || actionLoading === video.id}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-40"
                        >
                          Confirmer
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(video.id)}
                          disabled={actionLoading === video.id}
                          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
                        >
                          ✓ Approuver
                        </button>
                        <button
                          onClick={() => setRejectingId(video.id)}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          ✕ Rejeter
                        </button>
                      </>
                    )}
                  </div>
                )}

                {video.status === 'approved' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                      ✓ Approuvée
                    </span>
                    <button
                      onClick={() => handleUnpublish(video.id)}
                      disabled={actionLoading === video.id}
                      className="px-3 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-40"
                    >
                      Dépublier
                    </button>
                  </div>
                )}

                {video.status === 'rejected' && (
                  <span className="px-3 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200 flex-shrink-0">
                    ✕ Rejetée
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Lecteur plein écran (vidéo HTML5 / image) */}
      {playing && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setPlaying(null)}
        >
          <div className="relative max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16', maxHeight: '80vh' }}>
              {playing.mediaType === 'image' ? (
                <img src={playing.videoUrl} alt="" className="w-full h-full object-contain" />
              ) : playError ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                  <span className="text-4xl">⚠️</span>
                  <p className="text-white text-sm font-medium">Format non lisible dans ce navigateur</p>
                  <p className="text-white/60 text-xs">
                    Probablement un codec HEVC (vidéo iPhone). Essayez Safari, ou téléchargez le fichier.
                  </p>
                  <a
                    href={playing.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  >
                    Ouvrir / télécharger le fichier
                  </a>
                </div>
              ) : (
                <video
                  src={playing.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                  onError={() => setPlayError(true)}
                />
              )}
              {playing.textOverlay && (
                <div className="absolute left-0 right-0 top-1/3 flex justify-center pointer-events-none px-6">
                  <p className="bg-black/50 text-white text-sm font-bold rounded-lg px-3 py-1.5 text-center">
                    {playing.textOverlay}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-white text-sm font-medium">{playing.candidateName}</p>
              <div className="flex items-center gap-2">
                {playing.status === 'pending_review' && (
                  <>
                    <button
                      onClick={() => { handleApprove(playing.id); setPlaying(null); }}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                    >
                      ✓ Approuver
                    </button>
                    <button
                      onClick={() => { setRejectingId(playing.id); setPlaying(null); }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      ✕ Rejeter
                    </button>
                  </>
                )}
                <button
                  onClick={() => setPlaying(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoModeration;
